import { diagnostic } from '../utils/diagnostics';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  kidAuth,
  dbKid,
  dbKidData,
} from '../services/supabase';

const KidAuthContext = createContext(null);

export const useKidAuth = () => {
  const ctx = useContext(KidAuthContext);
  if (!ctx) throw new Error('useKidAuth must be used within KidAuthProvider');
  return ctx;
};

const KID_AUTH_INIT_TIMEOUT_MS = 15000;

export const KidAuthProvider = ({ children }) => {
  const [kidUser, setKidUser] = useState(null);
  const [currentKid, setCurrentKid] = useState(null);
  const [
  kidFamily,
  setKidFamily,
] = useState(null);

  // "loading" is kept for compatibility with the existing protected route.
  // It represents initial authentication startup only.
  const [loading, setLoading] = useState(true);
  const [kidLoading, setKidLoading] = useState(false);
  const [kidChecked, setKidChecked] = useState(false);
  const [kidLoadError, setKidLoadError] = useState(null);

  const [familyAccessState, setFamilyAccessState] = useState(null);
  const [familyAccessLoading, setFamilyAccessLoading] = useState(false);
  const [familyAccessError, setFamilyAccessError] = useState(null);
  const [familyOwnerName, setFamilyOwnerName] = useState('');

  const mountedRef = useRef(true);
  const profileLoadSeqRef = useRef(0);

  // Gate used by the existing kid sign-in/sign-out flow so the auth listener
  // does not fight with an in-progress explicit session change.
  const isSettingSessionRef = useRef(false);

  const loadKidProfile = async (
    user,
    { blockUI = false, silent = false } = {}
  ) => {
    if (!user?.id) {
      if (mountedRef.current) {
        setCurrentKid(null);
        setFamilyAccessState(null);
        setFamilyAccessLoading(false);
        setFamilyAccessError(null);
        setFamilyOwnerName('');
        setKidLoading(false);
        setKidChecked(true);
        setKidLoadError(null);
      }
      return null;
    }

    const mySeq = ++profileLoadSeqRef.current;

    if (blockUI && mountedRef.current) {
      setLoading(true);
    }

    if (mountedRef.current && !silent) {
      setKidLoading(true);
      setKidChecked(false);
      setKidLoadError(null);
    }

    diagnostic('debug', 'kid.profile.load_started');

    try {
      const kidRow = await dbKid.getKidByAuthUserId(user.id);

      if (!mountedRef.current || mySeq !== profileLoadSeqRef.current) {
        return null;
      }

      if (!kidRow) {
        try {
          window.sessionStorage.setItem(
            'mypiggy:kid-access-message',
            'Your child account is currently deactivated. Ask your Parent or Guardian to reactivate it.'
          );
        } catch {
          diagnostic('warn', 'kid.auth.storage_unavailable');
        }

        isSettingSessionRef.current = true;
        try {
          await kidAuth.signOut();
        } finally {
          isSettingSessionRef.current = false;
        }

        setKidUser(null);
        setCurrentKid(null);
        setKidFamily(null);
        setFamilyAccessState(null);
        setFamilyAccessLoading(false);
        setFamilyAccessError(null);
        setFamilyOwnerName('');
        setKidLoading(false);
        setKidChecked(true);
        setKidLoadError(null);
        return null;
      }

    diagnostic('debug', 'kid.profile.load_completed');

    const familyRow =
      await dbKidData.getFamilySettings(
        kidRow.family_id
      );

    let ownerName = '';

    try {
      const settingsSummary =
        await dbKidData.getKidSettingsSummary();

      const adults =
        Array.isArray(settingsSummary?.adults)
          ? settingsSummary.adults
          : [];

      const owner =
        adults.find(
          (adult) =>
            adult?.role === 'owner'
        );

      ownerName =
        owner?.name ||
        owner?.display_name ||
        '';
    } catch {
      diagnostic('warn', 'kid.profile.owner_lookup_failed');
    }

    let accessState = null;
    let accessError = null;

    try {
      setFamilyAccessLoading(true);
      accessState =
        await dbKidData.resolveMyFamilyAccessState();
    } catch (error) {
      accessError = error;
      diagnostic('error', 'kid.profile.access_lookup_failed');
    } finally {
      if (mountedRef.current) {
        setFamilyAccessLoading(false);
      }
    }

    if (
      !mountedRef.current ||
      mySeq !== profileLoadSeqRef.current
    ) {
      return null;
    }

    setCurrentKid(kidRow);
    setKidFamily(familyRow);
    setFamilyOwnerName(ownerName);
    setFamilyAccessState(accessState);
    setFamilyAccessError(accessError);

    return kidRow;
    } catch (error) {
      if (!mountedRef.current || mySeq !== profileLoadSeqRef.current) {
        return null;
      }

      const message = String(error?.message || '');
      let safeError = error;

      if (error?.name === 'AbortError' || message.includes('AbortError')) {
        safeError = new Error(
          'Loading your kid profile took too long. Please check your connection and try again.'
        );
      }

      diagnostic('error', 'kid.profile.load_failed');

      if (silent) {
        return null;
      }

      setCurrentKid(null);
      setKidLoadError(safeError);
      return null;
    } finally {
      if (
        mountedRef.current &&
        mySeq === profileLoadSeqRef.current &&
        !silent
      ) {
        setKidLoading(false);
        setKidChecked(true);
        if (blockUI) setLoading(false);
      }
    }
  };

  // Classify auth failures without serializing Supabase errors, tokens, or PII.
  // Classification is diagnostic only: session behavior is unchanged.
  const classifyKidInitError = (error) => {
    if (error?.name === 'AuthSessionMissingError') return 'SESSION_MISSING';
    if (error?.name === 'AbortError') return 'REQUEST_ABORTED';
    if (error?.name === 'TypeError') return 'NETWORK_OR_RUNTIME';
    if (error?.status === 401 || error?.status === 403) return 'AUTH_REJECTED';
    if (error?.status === 429) return 'RATE_LIMITED';
    if (typeof error?.status === 'number' && error.status >= 500 && error.status <= 599) {
      return 'AUTH_SERVICE_UNAVAILABLE';
    }
    return 'UNCLASSIFIED';
  };

  // Initialize the dedicated kid auth client once.
  useEffect(() => {
    mountedRef.current = true;
    let initFinished = false;

    const authInitTimeout = window.setTimeout(() => {
      if (!mountedRef.current || initFinished) return;

      const timeoutError = new Error(
        'Kid login initialization timed out. Please reload the page and try again.'
      );

      diagnostic('warn', 'kid.auth.init_timeout');
      setKidLoadError(timeoutError);
      setKidChecked(true);
      setLoading(false);
    }, KID_AUTH_INIT_TIMEOUT_MS);

    (async () => {
      try {
        diagnostic('debug', 'kid.auth.init_started');
        const { data, error } = await kidAuth.getUser();

        if (!mountedRef.current) return;
        if (error) throw error;

        const user = data?.user ?? null;
        diagnostic('debug', 'kid.auth.init_completed');

        setKidUser(user);

        if (!user) {
          setCurrentKid(null);
          setKidChecked(true);
          setKidLoadError(null);
        }
      } catch (error) {
        if (!mountedRef.current) return;

        const errorCode = classifyKidInitError(error);
        if (errorCode === 'SESSION_MISSING') {
          diagnostic('debug', 'kid.auth.no_saved_session');
        } else {
          diagnostic('error', 'kid.auth.init_failed', { error_code: errorCode });
        }
        setKidUser(null);
        setCurrentKid(null);
        setKidChecked(true);
        setKidLoadError(errorCode === 'SESSION_MISSING' ? null : error);
      } finally {
        initFinished = true;
        window.clearTimeout(authInitTimeout);

        if (mountedRef.current) {
          setLoading(false);
        }
      }
    })();

    const { data: sub } = kidAuth.onAuthStateChange((_event, session) => {
      if (!mountedRef.current || isSettingSessionRef.current) {
        return;
      }

      const user = session?.user ?? null;
      diagnostic('debug', 'kid.auth.state_changed');

      setKidUser(user);
      setKidLoadError(null);

      if (!user) {
        profileLoadSeqRef.current += 1;
        setCurrentKid(null);
        setKidFamily(null);
        setFamilyAccessState(null);
        setFamilyAccessLoading(false);
        setFamilyAccessError(null);
        setFamilyOwnerName('');
        setKidLoading(false);
        setKidChecked(true);
      }
    });

    return () => {
      mountedRef.current = false;
      window.clearTimeout(authInitTimeout);
      sub?.subscription?.unsubscribe();
    };
  }, []);

  // Load the child profile whenever the authenticated kid changes.
  useEffect(() => {
    if (loading) return;

    if (!kidUser) {
      setCurrentKid(null);
      setKidLoading(false);
      setKidChecked(true);
      return;
    }

    if (isSettingSessionRef.current) {
      return;
    }

    loadKidProfile(kidUser);
    // loadKidProfile intentionally depends only on the current authenticated kid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kidUser, loading]);

  // Revalidate an existing kid session when the app regains focus and
  // periodically while it remains open. RLS is still the immediate data
  // boundary; this check clears the stale client session and returns the
  // child to Kid Login after a parent deactivates the account.
  useEffect(() => {
    if (
      loading ||
      !kidUser ||
      !currentKid ||
      !kidChecked ||
      kidLoading
    ) {
      return undefined;
    }

    const revalidate = () => {
      if (
        document.visibilityState === 'visible' &&
        !isSettingSessionRef.current
      ) {
        loadKidProfile(kidUser, { silent: true });
      }
    };

    const intervalId = window.setInterval(
      revalidate,
      15000
    );

    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidate);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidate);
    };
  }, [
    kidUser,
    currentKid,
    kidChecked,
    kidLoading,
    loading,
  ]);

  const refreshKid = async ({ blockUI = false } = {}) => {
    if (!kidUser) {
      setCurrentKid(null);
      setKidFamily(null);
      setFamilyAccessState(null);
      setFamilyAccessLoading(false);
      setFamilyAccessError(null);
      setFamilyOwnerName('');
      setKidLoading(false);
      setKidChecked(true);
      setKidLoadError(null);
      return null;
    }

    return loadKidProfile(kidUser, { blockUI });
  };

  // Kid login uses the separate kid client/storage key.
  //
  // Stage 2B public authentication uses one MyPiggy Login ID
  // plus the kid's 6-digit PIN. The Edge Function resolves the
  // family from the Login ID and returns the internal synthetic
  // Supabase Auth credentials needed to establish the kid session.
  const loginKid = async ({ loginId, password: kidPassword }) => {
    diagnostic('debug', 'kid.auth.login_started');

    const {
      email,
      password,
    } = await dbKid.kidLogin({
      loginId,
      password: kidPassword,
    });

    diagnostic('debug', 'kid.auth.credentials_received');

    isSettingSessionRef.current = true;
    setCurrentKid(null);
    setKidChecked(false);
    setKidLoadError(null);

    try {
      const {
        data,
        error,
      } = await kidAuth.signIn(
        email,
        password || kidPassword
      );

      if (error) {
        diagnostic('error', 'kid.auth.login_failed');
        throw error;
      }

      diagnostic('info', 'kid.auth.login_completed');
      setKidUser(data?.user ?? null);
      return { success: true };
    } catch (error) {
      setKidChecked(true);
      setKidLoadError(error);
      throw error;
    } finally {
      isSettingSessionRef.current = false;
    }
  };

  const logoutKid = async () => {
    isSettingSessionRef.current = true;
    profileLoadSeqRef.current += 1;

    try {
      await kidAuth.signOut();
      setKidUser(null);
      setCurrentKid(null);
      setKidFamily(null);
      setFamilyAccessState(null);
      setFamilyAccessLoading(false);
      setFamilyAccessError(null);
      setFamilyOwnerName('');
      setKidLoading(false);
      setKidChecked(true);
      setKidLoadError(null);
      setLoading(false);
    } finally {
      isSettingSessionRef.current = false;
    }
  };

    const isFamilyInactive =
      kidFamily?.status != null && kidFamily.status !== 'active';

  const familyDeactivatedAt =
    kidFamily?.deactivated_at || null;

  const familyDeletionScheduledFor =
    kidFamily?.deletion_scheduled_for || null;

  const value = useMemo(
    () => ({
    kidUser,
    currentKid,
    kidFamily,

    isFamilyInactive,
    familyDeactivatedAt,
    familyDeletionScheduledFor,
    familyOwnerName,

    familyAccessState,
    familyAccessLoading,
    familyAccessError,
    plan: familyAccessState?.plan || null,
    entitlementStatus: familyAccessState?.entitlement_status || null,
    effectiveStatus: familyAccessState?.effective_status || null,
    canRead: familyAccessState?.can_read ?? null,
    canWrite: familyAccessState?.can_write ?? null,
    activeChildLimit: familyAccessState?.active_child_limit ?? null,
    trialEndsAt: familyAccessState?.trial_ends_at || null,
    currentPeriodEndsAt: familyAccessState?.current_period_ends_at || null,
    graceEndsAt: familyAccessState?.grace_ends_at || null,
    accessEndsAt: familyAccessState?.access_ends_at || null,

    loading,
    kidLoading,
    kidChecked,
    kidLoadError,

    isKidAuthenticated: !!kidUser,

    refreshKid,
    loginKid,
    logoutKid,
    }),
    [
    kidUser,
    currentKid,
    kidFamily,
    isFamilyInactive,
    familyDeactivatedAt,
    familyDeletionScheduledFor,
    familyOwnerName,
    familyAccessState,
    familyAccessLoading,
    familyAccessError,
    loading,
    kidLoading,
    kidChecked,
    kidLoadError,
    ]
  );

  return <KidAuthContext.Provider value={value}>{children}</KidAuthContext.Provider>;
};
